import { Link } from "wouter";
import { FileText, School, ShieldCheck, Link2, Globe, Users, Bell, Send, Mail, LayoutTemplate, BookOpen, Facebook, Bot, MessageCircle, Copy, Check, RefreshCw, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@clerk/react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { EmailBroadcastControl } from "@/components/admin/EmailBroadcastControl";

interface AdminLayoutProps {
  children: React.ReactNode;
  tab: "tenders" | "schools" | "schools-links" | "schools-directurl" | "members" | "schools-update" | "email-compose" | "email-templates" | "guides" | "meta" | "ai-assistant" | "ai-record";
}

type WhatsAppVariant = "free" | "member";

interface WhatsAppChannelDraft {
  chunks: string[];
  totalChars: number;
}

interface WhatsAppDraft {
  tenderCount: number;
  maxChars: number;
  free: WhatsAppChannelDraft;
  member: WhatsAppChannelDraft;
}

export default function AdminLayout({ children, tab }: AdminLayoutProps) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [sendingTg, setSendingTg] = useState(false);
  const [confirmPending, setConfirmPending] = useState<"telegram" | null>(null);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);
  const [whatsappDraft, setWhatsappDraft] = useState<WhatsAppDraft | null>(null);
  const [whatsappVariant, setWhatsappVariant] = useState<WhatsAppVariant>("free");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  async function doTelegramBroadcast() {
    setSendingTg(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/admin/telegram-broadcast", { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "發送失敗");
      const count = data.tenderCount ?? 0;
      const pubR = data.results?.public;
      const memR = data.results?.member;
      const pubOk = pubR?.ok;
      const memOk = memR?.ok;
      // tgSendChunked 回傳 { ok, sent, errors[] }；tgSend 回傳 { ok, description }
      const pubDetail = pubR?.sent != null ? `${pubR.sent} 段訊息` : (pubR?.description ?? pubR?.error ?? "");
      const memDetail = memR?.sent != null ? `${memR.sent} 段訊息` : (memR?.description ?? memR?.error ?? "");
      const pubErrStr = pubR?.errors?.join(", ") ?? "";
      const memErrStr = memR?.errors?.join(", ") ?? "";
      const lines: string[] = [`共 ${count} 項招標`];
      if (pubR == null) lines.push("📢 公開頻道：未設定");
      else if (pubOk) lines.push(`📢 公開頻道 ✅ ${pubDetail}`);
      else lines.push(`📢 公開頻道 ❌ ${pubErrStr || pubDetail}`);
      if (memR == null) lines.push("🔑 會員頻道：未設定");
      else if (memOk) lines.push(`🔑 會員頻道 ✅ ${memDetail}`);
      else lines.push(`🔑 會員頻道 ❌ ${memErrStr || memDetail}`);
      toast({
        title: (pubOk !== false && memOk !== false) ? "✅ Telegram 發送完成" : "⚠️ Telegram 部分失敗",
        description: lines.join("\n"),
        variant: (pubOk === false || memOk === false) ? "destructive" : "default",
      });
    } catch (e: any) {
      toast({ title: "❌ 發送失敗", description: e.message, variant: "destructive" });
    } finally {
      setSendingTg(false);
    }
  }


  async function loadWhatsAppDraft() {
    setWhatsappLoading(true);
    setWhatsappError(null);
    try {
      const token = await getToken();
      const res = await fetch("/api/admin/whatsapp-draft", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "讀取公告失敗");
      setWhatsappDraft(data as WhatsAppDraft);
    } catch (e: any) {
      setWhatsappError(e.message ?? "讀取公告失敗");
    } finally {
      setWhatsappLoading(false);
    }
  }

  function openWhatsAppDraft() {
    setWhatsappOpen(true);
    void loadWhatsAppDraft();
  }

  async function copyAnnouncement(text: string, key: string) {
    try {
      let copied = false;
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          copied = true;
        } catch {
          // Some browsers expose Clipboard API but reject it in an iframe or without permission.
          // Fall through to the legacy selection-based copy method.
        }
      }
      if (!copied) {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        copied = document.execCommand("copy");
        textarea.remove();
        if (!copied) throw new Error("瀏覽器拒絕複製");
      }
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((current) => current === key ? null : current), 1800);
      toast({ title: "已複製公告", description: "可以貼上到 WhatsApp Web 公告群組或 Channel。" });
    } catch (e: any) {
      toast({ title: "複製失敗", description: e.message ?? "請手動選取文字複製。", variant: "destructive" });
    }
  }

  async function handleConfirm() {
    const action = confirmPending;
    setConfirmPending(null);
    if (action === "telegram") await doTelegramBroadcast();
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-secondary/30 py-4">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <ShieldCheck className="h-5 w-5 text-primary" />
                <h1 className="text-xl font-bold">後台管理</h1>
              </div>
              <p className="text-muted-foreground text-sm">管理招標資料及學校資料庫</p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-sky-600 border-sky-300 hover:bg-sky-50 hover:text-sky-700"
                onClick={() => setConfirmPending("telegram")}
                disabled={sendingTg}
              >
                <Send className="h-4 w-4" />
                {sendingTg ? "發送中…" : "發送 Telegram"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-green-600 border-green-300 hover:bg-green-50 hover:text-green-700"
                onClick={openWhatsAppDraft}
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp 公告
              </Button>
              <EmailBroadcastControl />
            </div>
          </div>
        </div>
      </div>

      <div className="border-b bg-background">
        <div className="container mx-auto px-4 md:px-8">
          {/* Row 1 — core tabs */}
          <nav className="flex gap-1 -mb-px border-b border-transparent">
            <Link
              href="/admin/tenders"
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                tab === "tenders"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <FileText className="h-4 w-4" /> 招標管理
            </Link>
            <Link
              href="/admin/schools"
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                tab === "schools"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <School className="h-4 w-4" /> 學校管理
            </Link>
            <Link
              href="/admin/schools_links"
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                tab === "schools-links"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <Link2 className="h-4 w-4" /> 來源連結
            </Link>
            <Link
              href="/admin/schools_directurl"
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                tab === "schools-directurl"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <Globe className="h-4 w-4" /> 招標頁管理
            </Link>
            <Link
              href="/admin/members"
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                tab === "members"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <Users className="h-4 w-4" /> 會員管理
            </Link>
            <Link
              href="/admin/email-templates"
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                tab === "email-templates"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <LayoutTemplate className="h-4 w-4" /> 電郵範本
            </Link>
            <Link
              href="/admin/schools_update"
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                tab === "schools-update"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <Bell className="h-4 w-4" /> 網頁更新
            </Link>
          </nav>
          {/* Row 2 — content & social tabs */}
          <nav className="flex gap-1 -mb-px border-t border-border/50">
            <Link
              href="/admin/guides"
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                tab === "guides"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <BookOpen className="h-4 w-4" /> 採購知識
            </Link>
            <Link
              href="/admin/meta"
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                tab === "meta"
                  ? "border-[#1877f2] text-[#1877f2]"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <Facebook className="h-4 w-4" /> Meta 社交
            </Link>
            <Link
              href="/admin/ai-assistant"
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                tab === "ai-assistant"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <Bot className="h-4 w-4" /> AI 助手
            </Link>
            <Link
              href="/admin/ai-record"
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                tab === "ai-record"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <Activity className="h-4 w-4" /> AI 使用紀錄
            </Link>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {children}
      </div>

      <ConfirmDialog
        open={confirmPending !== null}
        title={confirmPending === "telegram" ? "發送 Telegram" : "發送電郵廣播"}
        description={
          confirmPending === "telegram"
            ? "確定發送今日招標到 Telegram 頻道？"
            : "確定發送今日招標電郵給所有會員？"
        }
        confirmLabel="確定發送"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmPending(null)}
      />

      <Dialog open={whatsappOpen} onOpenChange={setWhatsappOpen}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <MessageCircle className="h-5 w-5" />
              WhatsApp 公告助手
            </DialogTitle>
            <DialogDescription>
              產生純文字公告後，逐段複製並貼到 WhatsApp Web 的社群公告。這個功能不會自動發送訊息。
            </DialogDescription>
          </DialogHeader>

          {whatsappLoading ? (
            <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" />
              正在整理今日招標…
            </div>
          ) : whatsappError ? (
            <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              <p>{whatsappError}</p>
              <Button variant="outline" size="sm" onClick={() => void loadWhatsAppDraft()}>
                <RefreshCw className="mr-1.5 h-4 w-4" /> 重新載入
              </Button>
            </div>
          ) : whatsappDraft ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/30 p-3 text-sm">
                <span>
                  今日共 <strong>{whatsappDraft.tenderCount}</strong> 項招標
                </span>
                <span className="text-muted-foreground">
                  每段安全上限：{whatsappDraft.maxChars.toLocaleString()} 字
                </span>
              </div>

              <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
                {([
                  ["free", "招標公告（免費版）"],
                  ["member", "會員公告（會員版）"],
                ] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setWhatsappVariant(value)}
                    className={cn(
                      "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      whatsappVariant === value
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {(() => {
                const channel = whatsappDraft[whatsappVariant];
                return (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm text-muted-foreground">
                        共 {channel.chunks.length} 段 · {channel.totalChars.toLocaleString()} 字
                      </div>
                      {channel.chunks.length === 1 ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => void copyAnnouncement(channel.chunks[0], `${whatsappVariant}-all`)}
                        >
                          {copiedKey === `${whatsappVariant}-all` ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                          {copiedKey === `${whatsappVariant}-all` ? "已複製" : "複製全部公告"}
                        </Button>
                      ) : null}
                    </div>

                    <p className="text-xs text-muted-foreground">
                      每段均不超過上限；多段公告請使用下方各段的 Copy 按鈕，逐段貼上 WhatsApp。
                    </p>

                    {channel.chunks.map((chunk, index) => {
                      const key = `${whatsappVariant}-${index}`;
                      return (
                        <div key={key} className="space-y-2 rounded-lg border p-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium">第 {index + 1} 段</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{chunk.length.toLocaleString()} 字</span>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1.5"
                                onClick={() => void copyAnnouncement(chunk, key)}
                              >
                                {copiedKey === key ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                                {copiedKey === key ? "已複製" : "Copy"}
                              </Button>
                            </div>
                          </div>
                          <Textarea
                            value={chunk}
                            readOnly
                            rows={Math.min(18, Math.max(8, chunk.split("\n").length + 1))}
                            className="resize-y bg-muted/20 text-sm leading-relaxed"
                            aria-label={`WhatsApp ${whatsappVariant === "free" ? "免費版" : "會員版"}公告第 ${index + 1} 段`}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
